import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { z } from 'zod';
import {
    FaUser,
    FaUndo,
    FaBox,
    FaCreditCard,
    FaAngleDown,
    FaBoxOpen,
    FaTruck,
    FaTimes,
    FaSave,
} from 'react-icons/fa';


import { FaEdit } from 'react-icons/fa';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { IoIosCloseCircle } from "react-icons/io";
import { zodResolver } from '@hookform/resolvers/zod';
import { useMyContext } from '../../../context/MyContext';
import { FaWarehouse } from "react-icons/fa6";
import '../../css/custom-scrollbar.css';
import { Base_Url, updateShipment } from '../../../config/config';
import { toast } from 'react-toastify';

const schema = z.object({
    order_id: z.string().min(1, { message: 'Order ID is required' }),
    first_name: z.string().min(1, { message: 'First name is required' }),
    last_name: z.string().min(1, { message: 'Last name is required' }),
    // address: z.string().min(1, { message: 'Address is required' }),
    address: z
        .string()
        .trim()
        .min(10, "Full Address must be at least 10 characters")
        .regex(/^[^#\\%&;]*$/, "Address contains invalid characters (# \\ % & ; not allowed)")
        .refine((val) => val.trim().length >= 10, {
            message: "Address cannot be only spaces",
        }),

    phone: z.string().min(1, { message: 'Phone is required'}),
    payment_mode: z.string().min(1, { message: 'Payment mode is required' }),
    cod_amount: z.string().min(1, { message: 'COD amount is required' }),
    length: z.number().positive({ message: 'Length must be positive' }),
    shipment_width: z.number().positive({ message: 'Width must be positive' }),
    shipment_height: z.number().positive({ message: 'Height must be positive' }),
    weight: z.number().positive({ message: 'weight must be positive' }),
    warehouse_id: z.string().min(1, { message: 'Warehouse name is required' })
});

// Section badge -> icon wrapper with gradient background per section
const SectionBadge = ({ icon, gradient }) => (
    <div
        className={`flex items-center justify-center w-9 h-9 rounded-xl shadow-md bg-gradient-to-br ${gradient} text-white flex-shrink-0`}
    >
        {icon}
    </div>
);

const ViewAllShipment = ({ isopen, onClose, shipment, wareHouseList, fetchOrderData }) => {

    const { isDarkMode } = useMyContext();
    const [currentWarehouseName, setCurrentWarehouseName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            order_id: '',
            first_name: '',
            last_name: '',
            address: '',
            phone: '',
            payment_mode: '',
            cod_amount: '',
            length: '',
            shipment_width: '',
            shipment_height: '',
            weight: '',
            warehouse_id: ''
        }
    });

    const paymentMode = watch('payment_mode');

    // Check if a field is editable
    const isEditable = (fieldName) => {
        const editableFields = [
            'warehouse_id',
            'first_name', 'last_name', 'address',
            'phone', 'payment_mode', 'cod_amount', 'length',
            'shipment_width', 'shipment_height', 'weight',
        ];
        return editableFields.includes(fieldName) && isEditing;
    };

    useEffect(() => {
        if (shipment) {
            // Set basic form values
            setValue('order_id', shipment.order_id || '');
            setValue('first_name', shipment.first_name || '');
            setValue('last_name', shipment.last_name || '');
            setValue('address', shipment.address || '');
            setValue('phone', shipment.phone || '');
            setValue('payment_mode', shipment.payment_mode || '');
            setValue('cod_amount', shipment.cod_amount || shipment.cod || '');
            setValue('length', shipment.length || 0);
            setValue('shipment_width', shipment.shipment_width || 0);
            setValue('shipment_height', shipment.shipment_height || 0);
            setValue('weight', shipment.weight || 0);

            if (wareHouseList && wareHouseList.length > 0) {
                let selectedWarehouse = null;
                if (shipment.warehouse_id) {
                    selectedWarehouse = wareHouseList.find(w => w.value === shipment.warehouse_id);
                }
                if (selectedWarehouse) {
                    setValue('warehouse_id', selectedWarehouse.label);
                    setCurrentWarehouseName(selectedWarehouse.label); // Store for display
                } else {
                    setCurrentWarehouseName('Not specified');
                }
            }
        }
    }, [shipment, wareHouseList, setValue]);


    const onSubmit = async (data) => {
        const url = Base_Url + updateShipment;
        try {
            setIsLoading(true);
            setError(null);

            // Find the selected warehouse ID based on the warehouse name
            let selectedWarehouseId = shipment.warehouse_id;
            if (data.warehouse_id && wareHouseList) {
                const selectedWarehouse = wareHouseList.find(w => w.label === data.warehouse_id);
                if (selectedWarehouse) {
                    selectedWarehouseId = selectedWarehouse.value;
                }
            }
            const payload = {
                user_id: shipment.user_id, // Keep non-editable fields from shipment
                order_id: data.order_id,   // Use form data for editable fields
                customer: {
                    first_name: data.first_name,     // Updated
                    last_name: data.last_name,       // Updated
                    email: shipment.email,           // Keep original (not editable)
                    phone: data.phone,               // Updated
                    address_type: shipment.address_type, // Keep original (not editable)
                },
                droplocation: {
                    address: data.address,           // Updated
                    city: shipment.city,             // Keep original (not editable)
                    state: shipment.state,           // Keep original (not editable)
                    country: shipment.country,       // Keep original (not editable)
                    pin: shipment.pin,               // Keep original (not editable)
                },
                pickuplocation: {
                    warehouse_id: selectedWarehouseId, // Updated based on selected warehouse
                },
                product: {
                    product_desc: shipment.products_desc,    // Keep original (not editable)
                    quantity: shipment.quantity,             // Keep original (not editable)
                    total_amount: shipment.total_amount,     // Keep original (not editable)
                    cod_amount: data.cod_amount,             // Updated
                    payment_mode: data.payment_mode,         // Updated
                    shipment_type: shipment.shipment_type,   // Keep original (not editable)
                },
                dimension: {
                    length: data.length,                     // Updated
                    width: data.shipment_width,              // Updated
                    height: data.shipment_height,            // Updated
                    weight: parseInt(data.weight, 10)    // Keep original (not editable)
                },
            };

            if (data.payment_mode === 'prepaid') {
                delete payload.product.cod_amount;
            }

            // Call API to update shipment
            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            if (response.data.status === "SUCCESS") {
                toast.success(response.data.message);
                setIsEditing(false);
                setIsLoading(false);
                onClose();
                fetchOrderData();
            }
        } catch (err) {
            const apiError = err.response?.data;
            if (apiError?.messages) {
                // Show all field-level errors
                Object.values(apiError.messages).forEach(msg => {
                    toast.error(msg);
                });
                setIsLoading(false);
                setError(apiError.messages);
            } else {
                // Fallback error
                const errorMessage = apiError?.message;
                toast.error(errorMessage);
                setError(errorMessage);
                setIsLoading(false);
            }
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };


    const renderField = (label, fieldName, type = 'text', isNumeric = false) => {
        const editable = isEditable(fieldName);
        let value = shipment?.[fieldName] || '';

        if (fieldName === 'cod_amount' && !value && shipment?.cod) {
            value = shipment.cod;
        }

        if (fieldName === 'weight' && shipment?.weight) {
            value = shipment.weight;
        }
        return (
            <div>
                <label className="block text-[11px] mb-1.5 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</label>
                {editable ? (
                    <>
                        <input
                            type={type}
                            step={isNumeric ? "0.1" : undefined}
                            placeholder={`Enter ${label}`}
                            className={`px-3 py-2.5 text-sm rounded-xl border w-full outline-none transition-all duration-200 shadow-sm focus:shadow-md focus:ring-4 ${isDarkMode
                                    ? "bg-gray-800/70 border-gray-700 text-white placeholder-gray-500 focus:border-indigo-400 focus:ring-indigo-500/20"
                                    : "bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-indigo-400 focus:ring-indigo-500/15"
                                }`}
                            {...register(fieldName, isNumeric ? { valueAsNumber: true } : {})}
                        />
                        {errors[fieldName] && <p className="text-red-500 text-xs mt-1 font-medium">{errors[fieldName].message}</p>}
                    </>
                ) : (
                    <div className={`px-3 py-2.5 text-sm rounded-xl border w-full transition-colors ${isDarkMode
                            ? "bg-gray-800/40 border-gray-700/60 text-gray-100"
                            : "bg-gray-50 border-gray-100 text-gray-700"
                        }`}>
                        <span className="text-sm">{value || <span className="italic text-gray-400">Not specified</span>}</span>
                    </div>
                )}
            </div>
        );
    };

    const handleClose = () => {
        setIsEditing(false); // Reset edit mode when closing
        onClose();
    };

    if (!shipment) return null;

    return (

        <Dialog
            open={isopen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: ` rounded-3xl overflow-hidden ${isDarkMode ? "!bg-darkmodeColor" : "!bg-gray-50 rounded-2xl"}`
            }}
        >
            {/* Sticky gradient header */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-indigo-600 via-purple-600   to-violet-700 px-6 py-5 shadow-lg">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-white/70 font-semibold mb-0.5">Shipment</p>
                        <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                            #{shipment?.order_id || 'Details'}
                            {shipment?.status && (
                                <span className="text-[10px] font-medium px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white">
                                    {shipment.status}
                                </span>
                            )}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm transition-all hover:scale-105"
                    >
                        <IoIosCloseCircle size={22} className="text-white" />
                    </button>
                </div>
                {/* subtle bottom fade divider */}
                <div className="absolute -bottom-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            </div>

            <DialogContent className={`overflow-auto custom-scrollbar px-4 md:px-6 pb-6 pt-4 ${isDarkMode
                ? "dark:bg-darkmodeColor dark:text-white"
                : "bg-gray-50"
                }`}>

                {!isEditing && (
                    <div className="flex justify-end mb-4">
                        <button
                            type="button"
                            onClick={handleEdit}
                            disabled={shipment?.status !== 'Pending'}
                            className={`py-2.5 px-5 rounded-xl focus:outline-none focus:ring-4 font-semibold text-sm transition-all duration-200 flex items-center gap-2 shadow-sm ${shipment?.status === 'Pending'
                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 focus:ring-blue-500/20'
                                : `${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-white text-gray-400'} cursor-not-allowed border border-gray-200 dark:border-gray-700`
                                }`}
                        >
                            <FaEdit size={13} /> Edit Shipment
                        </button>
                    </div>
                )}

                <form className="space-y-5 font-sans" onSubmit={handleSubmit(onSubmit)}>

                    {/* Customer Information Section */}
                    <div className={`rounded-2xl p-4 md:p-5 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>

                        <div className="flex items-center gap-3 mb-5">
                            <SectionBadge icon={<FaUser size={14} />} gradient="from-purple-500 to-fuchsia-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Customer Information
                            </h3>
                        </div>

                        {/* Warehouse + Order ID */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                            {/* Warehouse Name */}
                            <div>
                                <label className="block text-[11px] mb-1.5 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Warehouse Name
                                </label>

                                {isEditing ? (
                                    <>
                                        <select
                                            {...register('warehouse_id', {
                                                required: 'Warehouse Name is required'
                                            })}
                                            className={`px-3 py-2.5 text-sm rounded-xl border w-full outline-none transition-all shadow-sm focus:shadow-md focus:ring-4
                        ${isDarkMode
                                                    ? "bg-gray-800/70 text-white border-gray-700 focus:border-indigo-400 focus:ring-indigo-500/20"
                                                    : "bg-white border-gray-200 focus:border-indigo-400 focus:ring-indigo-500/15"
                                                }`}
                                        >
                                            <option value="">Select Warehouse</option>
                                            {wareHouseList?.map((warehouse) => (
                                                <option
                                                    key={warehouse.value}
                                                    value={warehouse.label}
                                                >
                                                    {warehouse.label} - {warehouse.pincode}
                                                </option>
                                            ))}
                                        </select>

                                        {errors.warehouse_id && (
                                            <p className="text-red-500 text-xs mt-1 font-medium">
                                                {errors.warehouse_id.message}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <div
                                        className={`px-3 py-2.5 text-sm rounded-xl border w-full flex items-center gap-2 ${isDarkMode
                                                ? "bg-gray-800/40 border-gray-700/60 text-gray-100"
                                                : "bg-gray-50 border-gray-100 text-gray-700"
                                            }`}
                                    >
                                        <FaWarehouse className="text-indigo-400" size={12} />
                                        <span className="font-medium text-sm">
                                            {currentWarehouseName || 'Not specified'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Order ID */}
                            <div>
                                {renderField('Order ID', 'order_id')}
                            </div>
                        </div>

                        {/* Name */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {renderField('First Name', 'first_name')}
                            </div>
                            <div>
                                {renderField('Last Name', 'last_name')}
                            </div>
                        </div>

                        {/* Email + Phone */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {renderField('Email', 'email')}
                            </div>
                            <div>
                                {renderField('Phone', 'phone')}
                            </div>
                        </div>

                        {/* City / State / Pincode */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {renderField('City', 'city')}
                            </div>
                            <div>
                                {renderField('State', 'state')}
                            </div>
                        </div>

                        {/* Country */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {renderField('Pincode', 'pin')}
                            </div>
                            <div>
                                {renderField('Country', 'country')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 mt-4">
                            <div>
                                {renderField('Address', 'address')}
                            </div>
                        </div>

                    </div>


                    {/* Return Address Section */}
                    <div className={`rounded-2xl p-4 md:p-5 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-3 mb-5">
                            <SectionBadge icon={<FaUndo size={13} />} gradient="from-sky-500 to-blue-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Return Details
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                {renderField('Return Address', 'return_address')}
                            </div>
                            <div>
                                {renderField('Return Phone', 'return_phone')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {renderField('Return City', 'return_city')}
                            </div>
                            <div>
                                {renderField('Return State', 'return_state')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {renderField('Return Pincode', 'return_pin')}
                            </div>
                            <div>
                                {renderField('Return Country', 'return_country')}
                            </div>
                        </div>
                    </div>

                    {/* Shipment Details Section */}
                    <div className={`rounded-2xl p-4 md:p-5 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-3 mb-5">
                            <SectionBadge icon={<FaBox size={13} />} gradient="from-emerald-500 to-teal-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Shipment Details
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                {renderField('Length', 'length', 'number', true)}
                            </div>
                            <div>
                                {renderField('Width', 'shipment_width', 'number', true)}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {renderField('Height', 'shipment_height', 'number', true)}
                            </div>
                            <div>
                                {renderField('Weight (grams)', 'weight', 'number', true)}
                            </div>
                        </div>
                    </div>

                    {/* Payment Details Section */}
                    <div className={`rounded-2xl p-4 md:p-5 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-3 mb-5">
                            <SectionBadge icon={<FaCreditCard size={13} />} gradient="from-amber-500 to-orange-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Payment Details
                            </h3>
                        </div>

                        {/* Payment Mode Field */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[11px] mb-1.5 font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Payment Mode</label>
                                {isEditing ? (
                                    <>
                                        <div className="relative">
                                            <select
                                                {...register('payment_mode')}
                                                className={`px-3 py-2.5 text-sm rounded-xl border w-full outline-none appearance-none transition-all shadow-sm focus:shadow-md focus:ring-4 ${isDarkMode
                                                        ? "bg-gray-800/70 text-white border-gray-700 focus:border-indigo-400 focus:ring-indigo-500/20"
                                                        : "bg-white border-gray-200 focus:border-indigo-400 focus:ring-indigo-500/15"
                                                    }`}
                                            >
                                                <option value="">Select Mode</option>
                                                <option value="prepaid">Prepaid</option>
                                                <option value="cod">COD</option>
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                                                <FaAngleDown className="text-gray-400" size={12} />
                                            </div>
                                        </div>
                                        {errors.payment_mode && (
                                            <p className="text-red-500 text-xs mt-1 font-medium">{errors.payment_mode.message}</p>
                                        )}
                                    </>
                                ) : (
                                    <div className="px-3 py-2.5 text-sm rounded-xl border w-full bg-gradient-to-r from-amber-500 to-orange-600 border-transparent shadow-sm">
                                        <span className="font-semibold text-xs text-white uppercase tracking-wide">{shipment?.payment_mode || 'Not specified'}</span>
                                    </div>
                                )}
                            </div>

                            {/* COD Amount Field (Shown only if payment_mode is 'cod') */}
                            {paymentMode === 'cod' && (
                                <div>
                                    {renderField('COD Amount', 'cod_amount')}
                                </div>
                            )}
                        </div>
                    </div>


                    {/* Product Details Section */}
                    <div className={`rounded-2xl p-4 md:p-5 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-3 mb-5">
                            <SectionBadge icon={<FaBoxOpen size={13} />} gradient="from-indigo-500 to-violet-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Product Details
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                {renderField('Products Description', 'products_desc')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        </div>
                    </div>

                    {/* Pickup Information Section */}
                    <div className={`rounded-2xl p-4 md:p-5 border border-gray-300 transition-shadow hover:shadow-lg ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-3 mb-5">
                            <SectionBadge icon={<FaTruck size={13} />} gradient="from-rose-500 to-red-600" />
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                                Pickup Information
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                {renderField('Pickup Name', 'pickup_name')}
                            </div>
                            <div>
                                {renderField('Pickup Address', 'pickup_address')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {renderField('Pickup City', 'pickup_city')}
                            </div>
                            <div>
                                {renderField('Pickup Pin Code', 'pickup_pin_code')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                {renderField('Pickup Phone', 'pickup_phone')}
                            </div>
                            <div>
                                {renderField('Address Type', 'address_type')}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons - sticky footer style */}
                    {/* <div className={`sticky bottom-0 -mx-4 md:-mx-6 px-4 md:px-6 py-4 mt-2 flex flex-wrap justify-end gap-3 border-t backdrop-blur-md ${isDarkMode ? "bg-darkmodeColor/90 border-gray-800" : "bg-gray-50/90 border-gray-200"}`}>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="w-full sm:w-auto bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 py-2.5 px-6 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-4 focus:ring-gray-500/10 font-semibold text-sm transition-all flex items-center justify-center gap-2"
                        >
                            <FaTimes size={13} /> Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading && isEditing}
                            className={`
                                    w-full sm:w-auto
                                    bg-gradient-to-r from-purple-600 to-indigo-700 
                                    text-white py-2.5 px-6 rounded-xl 
                                    hover:shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-0.5
                                    focus:outline-none focus:ring-4 focus:ring-purple-500/20
                                    font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 shadow-sm
                                    disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:hover:translate-y-0
                                `}>
                            {isLoading && isEditing ? (
                                <>
                                    <svg className="animate-spin -ml-1 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave size={13} /> Save Changes
                                </>
                            )}
                        </button>
                    </div> */}

                </form>
            </DialogContent>

        </Dialog>

    );
};

export default ViewAllShipment;